import { useCallback, useRef, useState } from 'react';
import { getAuthToken } from '@/api/client';

export interface SSETokenEvent {
  type: 'token';
  content: string;
}

export interface SSECardEvent {
  type: 'card';
  card_type: string;
  card_id: string;
  data: Record<string, unknown>;
}

export interface SSEToolCallEvent {
  type: 'tool_call';
  tool: string;
  status: 'calling' | 'done';
}

export interface SSEDoneEvent {
  type: 'done';
  conversation_id: string;
}

export interface SSEErrorEvent {
  type: 'error';
  message: string;
}

export interface SSESuggestionsEvent {
  type: 'suggestions';
  suggestions: string[];
}

export type SSEEvent =
  | SSETokenEvent
  | SSECardEvent
  | SSEToolCallEvent
  | SSESuggestionsEvent
  | SSEDoneEvent
  | SSEErrorEvent;

interface UseAgentStreamOptions {
  onToken?: (content: string) => void;
  onCard?: (card: SSECardEvent) => void;
  onToolCall?: (event: SSEToolCallEvent) => void;
  onSuggestions?: (event: SSESuggestionsEvent) => void;
  onDone?: (event: SSEDoneEvent) => void;
  onError?: (message: string) => void;
}

export function useAgentStream(options: UseAgentStreamOptions = {}) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const startStream = useCallback(
    async (url: string, body: Record<string, unknown>) => {
      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);
      setStreamingContent('');

      try {
        const token = getAuthToken();
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = errorText;

          try {
            const parsed = JSON.parse(errorText);
            if (typeof parsed?.detail === 'string') {
              errorMessage = parsed.detail;
            } else if (typeof parsed?.detail?.message === 'string') {
              errorMessage = parsed.detail.message;
            } else if (typeof parsed?.message === 'string') {
              errorMessage = parsed.message;
            }
          } catch {
            // keep raw text
          }

          throw new Error(`HTTP ${response.status}: ${errorMessage}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
              try {
                const data = JSON.parse(line.slice(6));

                switch (currentEvent) {
                  case 'token':
                    setStreamingContent((prev) => prev + data.content);
                    options.onToken?.(data.content);
                    break;
                  case 'suggestions':
                    options.onSuggestions?.({ type: 'suggestions', ...data });
                    break;
                  case 'card':
                    options.onCard?.({ type: 'card', ...data });
                    break;
                  case 'tool_call':
                  case 'tool_result':
                    options.onToolCall?.({ type: 'tool_call', ...data });
                    break;
                  case 'done':
                    options.onDone?.({ type: 'done', ...data });
                    break;
                  case 'error':
                    options.onError?.(data.message);
                    break;
                }
              } catch {
                // skip malformed JSON
              }
              currentEvent = '';
            } else if (line === '') {
              currentEvent = '';
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        const message = err instanceof Error ? err.message : 'Stream failed';
        options.onError?.(message);
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [options],
  );

  const stopStream = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  return {
    isStreaming,
    streamingContent,
    setStreamingContent,
    startStream,
    stopStream,
  };
}
